import React, {memo} from "react";
import {get} from "lodash";
import {UserPermissionEnum} from "../../../utils/types/user";

import {BarChart} from "../../../atoms/admin-reporting/atoms/bar-chart";
import {
  AggregationType,
  AnalyticsType,
  ReportingContextType,
} from "../../../store/reporting/reporting.common";
import {RequiredUserPermission} from "../../../atoms/admin-user-permissions/RequiredUserPermission";
import {ReportingContext} from "../ReportingContext";
import {
  ChartProps,
  Props,
  MapNames,
  AnalyticsDataStateToBarStack,
  useOnDataPathChange,
  withCompare,
  createDataToBarStack,
} from "./helpers";
import {CompareDateLabel} from "../../../atoms/reporting-compare";
import {CardTitle} from "../../../atoms/card-title";
import {CompareDateLabelsWrapper} from "../reports/Report.common";

const mapAdmissionCompletionStatusNames: MapNames = name => {
  switch (name) {
    case "OPEN":
      return "Open";

    case "COMPLETED":
      return "Completed";

    case "DELETED":
      return "Deleted";

    default:
      return "Unknown";
  }
};

const dataProcess_AnalyticsAdmissionsCompletionOverall: AnalyticsDataStateToBarStack =
  createDataToBarStack(
    AggregationType.SUM,
    AggregationType.SUM,
    mapAdmissionCompletionStatusNames,
    (x: number) => `${x}`,
    true,
  );

const AnalyticsAdmissionsCompletionOverallChart = memo(
  ({data, isFullReport}: ChartProps) => {
    const {stack, max} = dataProcess_AnalyticsAdmissionsCompletionOverall(data);

    return (
      <BarChart
        data={stack}
        max={max}
        labelLeft="Completion Status"
        labelRight="Value"
        full={isFullReport}
        leftSortCustomMap={{
          Open: 2,
          Completed: 1,
          Deleted: 0,
        }}
        defaultSortColumn="left"
        tickCount={max < 5 ? 2 : undefined}
        axTickFormat={x => `${Math.floor(x)}`}
        hoverDataPreprocessor={(values, line, index) => {
          const sum = get(
            line.original[index].aggregations,
            AggregationType.SUM,
            0,
          );

          return [`${sum}/${max}`];
        }}
      />
    );
  },
);

export const ADMISSIONS_COMPLETION_OVERALL_CHART_ID =
  "completion-overall" as const;

export const AnalyticsAdmissionsCompletionOverall = (props: Props) => (
  <RequiredUserPermission permission={UserPermissionEnum.COMPLETION_TIME}>
    <div className="chart-header">
      <CardTitle
        title="Admissions Completion Overall"
        subtitle="by Admissions"
      />
      <CompareDateLabelsWrapper>
        <CompareDateLabel dateRange={props.dateRange} global />
      </CompareDateLabelsWrapper>
    </div>
    <ReportingContext
      dataPaths={withCompare({
        what: ReportingContextType.analyticsWithoutResolution,
        type: AnalyticsType.admissionsCompletionOverall,
        facilities: props.facilities,
        query: {...props.dateRange},
      })}
      onDataPathChange={useOnDataPathChange(props.reportingBlockSetters)}
      isFullReport={props.reportingBlockSetters.isFullReport}
      enabled={props.reportingBlockSetters.enabled}
    >
      {(data, isFullReport) => (
        <AnalyticsAdmissionsCompletionOverallChart
          data={data}
          isFullReport={isFullReport}
        />
      )}
    </ReportingContext>
  </RequiredUserPermission>
);
